import React, { useState } from 'react';
import { fetchAddressOverrides } from '../services/api';
import { CustomerPicker, EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Shipping Address Overrides: a customer's address and the address to ship to in its
 * place (Azure exit WS8). Reads sql01.ShippingAddressOverrides. Saving is off until
 * ws8_address_overrides_live.
 */

const showDate = (ymd) => { const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };

export default function AddressOverrides() {
  const live = useWriteLive('ws8_address_overrides');
  const [customerName, setCustomerName] = useState('');
  const [email, setEmail] = useState('');
  const [edit, setEdit] = useState(null);
  const { rows, error, reload } = useRows(() => fetchAddressOverrides({ customerName, email }), [customerName, email]);
  const { save, saving, error: saveError, setError } = useSave(reload);

  const open = (r) => { setError(null); setEdit(r ? { ...r, note: r.note || '' } : { id: 0, originalAddress: '', overrideAddress: '', note: '' }); };
  const submit = async () => {
    if (!edit.contactId) return setError('Please select a customer.');
    if (!edit.originalAddress.trim() || !edit.overrideAddress.trim()) return setError('Please enter all required fields.');
    const body = { id: edit.id, contactId: edit.contactId, originalAddress: edit.originalAddress, overrideAddress: edit.overrideAddress, note: edit.note };
    if (await save('address_override_save', body)) setEdit(null);
    return null;
  };
  const remove = (r) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Do you want to delete?')) save('address_override_delete', { id: r.id });
  };
  const text = (key, label) => <Field label={label}><input className="form-input" maxLength={255} value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} /></Field>;

  return (
    <div>
      <PageTitle>Shipping Address Overrides</PageTitle>
      <WriteOffNote live={live} />
      <div className="port-filters">
        <input className="form-input" style={{ width: 'auto' }} placeholder="Customer name" aria-label="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        <input className="form-input" style={{ width: 'auto' }} placeholder="Email" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => open(null)}>Add Override</button>
      </div>
      <ListCard title="Overrides" rows={rows} error={error} empty="No overrides.">
        <table className="trans-table">
          <thead><tr><th>Customer</th><th>Original Address</th><th>Override Address</th><th>Note</th><th>Added</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.firstName} {r.lastName}<br />{r.email}</td>
                <td>{r.originalAddress}</td>
                <td>{r.overrideAddress}</td>
                <td>{r.note}</td>
                <td>{showDate(r.createdAt)}</td>
                <td className="staff-actions">
                  <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => open(r)}>Edit</button>
                  <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => remove(r)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {!edit && saveError && <div className="staff-msg staff-msg-error">{saveError}</div>}
      {edit && (
        <EditDialog title={edit.id ? 'Edit Override' : 'Add Override'} live={live} saving={saving} error={saveError} onClose={() => setEdit(null)} onSave={submit}>
          {edit.contactId
            ? <div className="staff-store"><strong>{edit.firstName} {edit.lastName}</strong><br />{edit.email}</div>
            : <CustomerPicker onPick={(c) => setEdit({ ...edit, ...c })} />}
          {edit.contactId && <>{text('originalAddress', 'Original Address')}{text('overrideAddress', 'Override Address')}{text('note', 'Note')}</>}
        </EditDialog>
      )}
    </div>
  );
}
